import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ChevronRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Discreet gold "?" mini-button placed next to a field label.
 * Click → opens a tooltip with a short explanation + a deep-link to the
 * relevant tutorial section.
 *
 * Usage:
 *   <FieldHelp
 *     id="ir-rate"
 *     title="Taux d'IR effectif"
 *     description="Pourcentage moyen prélevé sur le revenu annuel..."
 *     chapter={3}
 *     section="section-3-3"
 *   />
 */
export default function FieldHelp({
  id,
  title,
  description,
  chapter,
  section,
}) {
  const target = section
    ? `/tutorial/chapter/${chapter}#${section}`
    : `/tutorial/chapter/${chapter}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Aide sur le champ ${title}`}
          data-testid={`field-help-${id}`}
          className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-brand-gold transition-colors hover:bg-brand-gold/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-72 rounded-xl border border-brand-gold/40 bg-card p-3 text-sm shadow-xl"
        data-testid={`field-help-${id}-content`}
      >
        <div className="text-[11px] font-bold uppercase tracking-widest text-brand-gold">
          {title}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-foreground/85">
          {description}
        </p>
        <Link
          to={target}
          data-testid={`field-help-${id}-link`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-blue hover:underline dark:text-brand-gold"
        >
          Voir la section tutoriel
          <ChevronRight className="h-3 w-3" />
        </Link>
      </PopoverContent>
    </Popover>
  );
}
